import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferencesContext';

export default function Settings() {
  const { user, logout, addGoogleAccount } = useAuth();
  const { theme, setTheme } = useTheme();
  const { preferences, updatePreferences } = usePreferences();

  return (
    <div className="space-y-6 pb-20 lg:pb-0 max-w-2xl">
      {/* Header */}
      <header>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Settings</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">Customize your experience</p>
      </header>

      {/* Profile */}
      <section className="card">
        <h2 className="text-lg font-semibold mb-4">Profile</h2>
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {user?.name?.charAt(0) || 'W'}
              </span>
            </div>
          )}
          <div>
            <p className="font-semibold text-lg">{user?.name}</p>
            <p className="text-neutral-500 dark:text-neutral-400">{user?.email}</p>
          </div>
        </div>
      </section>

      {/* Connected Accounts */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Connected Accounts</h2>
          <button onClick={addGoogleAccount} className="btn-primary text-sm py-2">
            <PlusIcon />
            Add Account
          </button>
        </div>

        <div className="space-y-3">
          {user?.googleAccounts?.map((account) => (
            <div
              key={account.id}
              className="flex items-center gap-3 p-3 rounded-soft bg-neutral-50 dark:bg-neutral-800"
            >
              <GoogleIcon />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{account.email}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{account.label}</p>
              </div>
              {account.isPrimary && (
                <span className="badge-primary text-xs">Primary</span>
              )}
            </div>
          )) || (
            <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
              No additional accounts connected
            </p>
          )}
        </div>

        {/* OFW Connection */}
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">👨‍👩‍👧</span>
              <div>
                <p className="font-medium">OurFamilyWizard</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Sync custody schedule & messages
                </p>
              </div>
            </div>
            <button className="btn-secondary text-sm py-2">Connect</button>
          </div>
        </div>
      </section>

      {/* Appearance */}
      <section className="card">
        <h2 className="text-lg font-semibold mb-4">Appearance</h2>

        <div className="space-y-4">
          {/* Theme */}
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 block">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['light', 'dark', 'system'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  className={`p-3 rounded-soft text-sm font-medium transition-colors ${
                    theme === t
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                  }`}
                >
                  {t === 'light' && '☀️ '}
                  {t === 'dark' && '🌙 '}
                  {t === 'system' && '💻 '}
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Compact Mode */}
          <SettingToggle
            label="Compact Mode"
            description="Reduce spacing for more content"
            checked={preferences.compactMode}
            onChange={(checked) => updatePreferences({ compactMode: checked })}
          />
        </div>
      </section>

      {/* Accessibility */}
      <section className="card">
        <h2 className="text-lg font-semibold mb-4">Accessibility</h2>

        <div className="space-y-4">
          <SettingToggle
            label="Reduced Motion"
            description="Minimize animations throughout the app"
            checked={preferences.reducedMotion}
            onChange={(checked) => updatePreferences({ reducedMotion: checked })}
          />

          <SettingToggle
            label="Focus Mode"
            description="Dim non-essential UI when focusing"
            checked={preferences.focusMode}
            onChange={(checked) => updatePreferences({ focusMode: checked })}
          />
        </div>
      </section>

      {/* Notifications */}
      <section className="card">
        <h2 className="text-lg font-semibold mb-4">Notifications</h2>

        <div className="space-y-4">
          <SettingToggle
            label="Notification Sounds"
            description="Play sounds for new notifications"
            checked={preferences.notificationSound}
            onChange={(checked) => updatePreferences({ notificationSound: checked })}
          />

          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 block">
              Default View
            </label>
            <select
              value={preferences.defaultView}
              onChange={(e) => updatePreferences({ defaultView: e.target.value as typeof preferences.defaultView })}
              className="input"
            >
              <option value="dashboard">Dashboard</option>
              <option value="children">Children</option>
              <option value="inbox">Inbox</option>
              <option value="finances">Finances</option>
            </select>
          </div>
        </div>
      </section>

      {/* Webhooks */}
      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Webhooks</h2>
          <button className="btn-secondary text-sm py-2">
            <PlusIcon />
            Add Webhook
          </button>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
          No webhooks configured. Add webhooks to receive notifications from external services.
        </p>
      </section>

      {/* Danger Zone */}
      <section className="card border-red-200 dark:border-red-900">
        <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>

        <div className="space-y-3">
          <button
            onClick={logout}
            className="w-full btn bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
          >
            Sign Out
          </button>
          <button className="w-full btn btn-ghost text-red-600 dark:text-red-400">
            Delete Account
          </button>
        </div>
      </section>

      {/* Version info */}
      <footer className="text-center text-sm text-neutral-400 dark:text-neutral-600 py-4">
        <p>WEZ v1.0.0</p>
        <p className="mt-1">Built with care for neurodivergent minds</p>
      </footer>
    </div>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-7 rounded-full transition-colors ${
          checked ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'
        }`}
      >
        <span
          className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
